import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { ArrowRight, Calendar, Heart, Users, Play, ChevronRight } from "lucide-react";
import { api, HERO_BG_IMG } from "../lib/api";
import { Container, Overline, SectionHeading } from "../components/Section";

const SERVICE_TIMES = [
  { day: "Sunday", title: "Worship Service", time: "10:00 AM", location: "Main Sanctuary" },
  { day: "Wednesday", title: "Bible Study", time: "7:00 PM", location: "Fellowship Hall" },
  { day: "Saturday", title: "Morning Prayer", time: "7:00 AM", location: "Prayer Chapel" },
];

export default function Home() {
  const [events, setEvents] = useState([]);
  const [ministries, setMinistries] = useState([]);

  useEffect(() => {
    // Only show future/today events on the homepage
    const todayStr = new Date().toISOString().slice(0, 10);
    api.get("/events")
      .then((r) => setEvents(r.data.filter((e) => e.event_date >= todayStr).slice(0, 4)))
      .catch(() => {});
    api.get("/ministries").then((r) => setMinistries(r.data.slice(0, 6))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="home-hero">
        <div className="absolute inset-0 z-0">
          <img
            src={HERO_BG_IMG}
            alt="Worship"
            className="w-full h-full object-cover animate-slow-zoom"
            loading="eager"
            fetchpriority="high"
            decoding="async"
          />
          <div className="absolute inset-0" style={{
            background: "linear-gradient(180deg, rgba(10,5,8,0.55) 0%, rgba(27,10,31,0.30) 40%, rgba(58,14,31,0.50) 80%, #0A0508 100%)",
          }} />
          <div className="absolute inset-0 bg-grain" />
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 w-full pt-24 pb-20">
          <motion.div
            initial="hidden" animate="show"
            variants={{ hidden: {}, show: { transition: { staggerChildren: 0.15 } } }}
            className="max-w-5xl"
          >
            <motion.div variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } }}>
              <Overline>Sixth Mount Zion Baptist Temple · Hampton, VA</Overline>
            </motion.div>
            <motion.h1
              variants={{ hidden: { opacity: 0, y: 30 }, show: { opacity: 1, y: 0, transition: { duration: 0.9 } } }}
              className="text-[3.2rem] sm:text-7xl md:text-8xl font-sans font-black leading-[0.92] tracking-[-0.03em] text-balance"
              data-testid="hero-headline"
            >
              Loving God,
              <br />
              Loving Others
              <br />
              <span className="brand-gradient-text font-black">& Serving</span> the World.
            </motion.h1>
            <motion.p
              variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } }}
              className="mt-10 text-lg md:text-xl text-white/85 max-w-2xl leading-relaxed text-pretty font-light"
            >
              We exist to lead people to Christ and help them grow through meaningful relationships, authentic worship, and solid biblical truth. <span className="brand-gradient-text font-semibold">Where Miracles Live.</span>
            </motion.p>
            <motion.div
              variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } }}
              className="mt-12 flex flex-wrap gap-4"
            >
              <Link to="/about" className="btn-wine px-8 py-4 rounded-full text-sm uppercase tracking-[0.2em] font-bold inline-flex items-center gap-2" data-testid="hero-learn-more-btn">
                Learn More <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/calendar" className="btn-ghost px-8 py-4 rounded-full text-sm uppercase tracking-[0.2em] font-bold inline-flex items-center gap-2" data-testid="hero-plan-visit-btn">
                Plan a Visit
              </Link>
            </motion.div>
          </motion.div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/40 text-xs uppercase tracking-[0.4em] z-10">Scroll</div>
      </section>

      {/* SERVICE TIMES */}
      <section className="border-y border-white/5 py-12" style={{ background: "linear-gradient(180deg, rgba(91,42,134,0.14), rgba(184,50,128,0.06))" }}>
        <Container>
          <div className="grid md:grid-cols-3 gap-8 md:gap-16">
            {SERVICE_TIMES.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="flex flex-col gap-2"
              >
                <div className="text-xs uppercase tracking-[0.3em] text-ember font-bold">{s.day}</div>
                <div className="font-sans text-3xl md:text-4xl font-extrabold leading-tight">{s.title}</div>
                <div className="text-sm text-white/65 mt-1">{s.time} · {s.location}</div>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* MISSION */}
      <section className="py-32" data-testid="home-mission">
        <Container>
          <div className="grid lg:grid-cols-12 gap-16 items-center">
            <div className="lg:col-span-7">
              <SectionHeading
                overline="Welcome Home"
                title={<>A Place to <span className="brand-gradient-text">Belong</span>, <br/>Believe, & Become.</>}
                kicker="For generations, the Sixth Mount Zion family has been a beacon of hope in the Hampton Roads community. We are a Bible-teaching, Spirit-filled, multi-generational congregation passionate about the gospel of Jesus Christ."
              />
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/history" className="btn-ghost px-6 py-3 rounded-full text-sm uppercase tracking-wider font-bold inline-flex items-center gap-2">
                  Our History <ChevronRight className="h-4 w-4" />
                </Link>
                <Link to="/pastor" className="btn-ghost px-6 py-3 rounded-full text-sm uppercase tracking-wider font-bold inline-flex items-center gap-2">
                  Meet the Pastor <ChevronRight className="h-4 w-4" />
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5 grid grid-cols-2 gap-4">
              {[
                { icon: Users, label: "Active Members", value: "1,200+", to: "/members/new" },
                { icon: Heart, label: "Ministries", value: "20+", to: "/ministries" },
                { icon: Calendar, label: "Years of Faith", value: "100+", to: "/history" },
                { icon: Play, label: "Sermons Online", value: "500+", to: "/media-library" },
              ].map((it, i) => (
                <motion.div
                  key={it.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                >
                  <Link to={it.to} className="glass p-8 hover:bg-white/[0.08] hover:border-gold/30 transition-all block group" data-testid={`stat-${it.label.toLowerCase().replace(/\s+/g, '-')}`}>
                    <it.icon className="h-5 w-5 text-ember mb-4 stroke-[1.5] group-hover:text-gold transition-colors" />
                    <div className="font-sans text-4xl font-black">{it.value}</div>
                    <div className="text-xs uppercase tracking-widest text-white/50 mt-2 font-bold group-hover:text-ember transition-colors">{it.label}</div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* MINISTRIES */}
      <section className="py-24 bg-wine-950" data-testid="home-ministries">
        <Container>
          <div className="flex items-end justify-between mb-14 flex-wrap gap-6">
            <SectionHeading overline="Get Involved" title={<>Find Your <span className="brand-gradient-text">Ministry</span></>} />
            <Link to="/ministries" className="text-sm uppercase tracking-widest text-ember hover:text-ember-light inline-flex items-center gap-2 font-bold">
              View All <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5">
            {ministries.map((m, i) => (
              <Link
                key={m.id}
                to="/ministries"
                className="bg-wine-950 p-10 hover:bg-wine-900 transition-colors group min-h-[280px] flex flex-col"
                data-testid={`ministry-card-${m.slug}`}
              >
                <div className="w-12 h-1 brand-gradient mb-6 group-hover:w-20 transition-all duration-300" />
                <h3 className="font-sans text-2xl font-extrabold mb-3 group-hover:text-ember transition-colors">{m.name}</h3>
                <p className="text-sm text-white/65 leading-relaxed flex-1">{m.description}</p>
                <div className="mt-6 text-xs uppercase tracking-widest text-ember/80 group-hover:text-ember transition-colors inline-flex items-center gap-2 font-bold">
                  Learn More <ArrowRight className="h-3 w-3 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </Container>
      </section>

      {/* EVENTS */}
      <section className="py-32" data-testid="home-events">
        <Container>
          <div className="flex items-end justify-between mb-14 flex-wrap gap-6">
            <SectionHeading overline="What's Happening" title={<>Upcoming <span className="brand-gradient-text">Events</span></>} />
            <Link to="/calendar" className="text-sm uppercase tracking-widest text-ember hover:text-ember-light inline-flex items-center gap-2 font-bold">
              Full Calendar <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {events.map((e) => (
              <Link to="/calendar" key={e.id} className="glass p-8 hover:bg-white/[0.06] transition-colors group" data-testid={`event-card-${e.id}`}>
                <div className="text-xs uppercase tracking-widest text-ember mb-3 font-bold">{e.category}</div>
                <h4 className="font-sans text-xl font-extrabold mb-3 group-hover:text-ember transition-colors">{e.title}</h4>
                <div className="text-sm text-white/60 space-y-1">
                  <div>{new Date(e.event_date).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</div>
                  <div>{e.start_time} · {e.location}</div>
                </div>
              </Link>
            ))}
          </div>
        </Container>
      </section>

      {/* GIVE CTA */}
      <section className="relative py-32 overflow-hidden" data-testid="home-give-cta">
        <div className="absolute inset-0 hero-bg" />
        <div className="absolute inset-0 bg-grain" />
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative z-10 max-w-3xl"
          >
            <Overline>Generosity</Overline>
            <h2 className="text-5xl md:text-7xl font-sans font-black leading-[0.95] text-balance tracking-[-0.03em]">
              Sow into <span className="brand-gradient-text">Kingdom</span> work.
            </h2>
            <p className="mt-8 text-lg text-white/85 leading-relaxed max-w-xl font-light">
              Your generosity empowers ministry, missions, music, and our community outreach throughout Hampton, Virginia and beyond.
            </p>
            <Link to="/donate" className="btn-wine px-10 py-4 rounded-full text-sm uppercase tracking-[0.2em] font-bold inline-flex items-center gap-3 mt-10" data-testid="home-cta-give-btn">
              Give Now <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>
        </Container>
      </section>
    </div>
  );
}
